import React, { createContext, useContext, useMemo, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeModeContext = createContext({ toggleTheme: () => {}, mode: 'light' });

export const useThemeMode = () => useContext(ThemeModeContext);

const buildTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: '#0BA480',
        dark: '#059669',
        deep: '#047857',
      },
      background: {
        default: mode === 'light' ? '#F8FAFC' : '#0a0a0a',
        paper:   mode === 'light' ? '#FFFFFF'  : '#141414',
      },
      text: {
        primary:   mode === 'light' ? '#0F172A' : '#F1F5F9',
        secondary: mode === 'light' ? '#334155' : '#CBD5E1',
      },
      divider: mode === 'light' ? '#E2E8F0' : '#334155',
      action: {
        hover: mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
      },
      custom: {
        muted:    mode === 'light' ? '#64748B' : '#94A3B8',
        muted2:   mode === 'light' ? '#94A3B8' : '#64748B',
        softCard: mode === 'light' ? '#F1F5F9' : '#111111',
        border:   mode === 'light' ? '#E2E8F0' : 'rgba(255,255,255,0.07)',
        dark:     '#071A2F',
      },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { transition: 'background-color 0.25s ease, color 0.25s ease' },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { transition: 'background-color 0.25s ease, border-color 0.25s ease' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { transition: 'background-color 0.25s ease' },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            '&.Mui-focused': { color: theme.palette.text.primary },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.secondary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.primary,
            },
          }),
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: ({ theme }) => ({ color: theme.palette.text.secondary }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            '&:hover': { backgroundColor: theme.palette.action.hover },
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            transition: 'background-color 0.25s ease',
          }),
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }),
        },
      },
    },
  });

export const AppThemeProvider = ({ children }) => {
  const stored = localStorage.getItem('zenopay-theme');
  const [mode, setMode] = useState(stored === 'dark' ? 'dark' : 'light');

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('zenopay-theme', next);
      return next;
    });
  };

  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={{ toggleTheme, mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
